import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { StatusChip } from "@/components/common/StatusChip";
import { PurchaseDialog } from "@/features/finance/PurchaseDialog";
import { VoidDialog } from "@/features/finance/VoidDialog";

const STATUS_TONE = { sin_asignar: "amber", asignada: "green", prorrateada: "blue" };

const ageClass = (fecha) => {
  const dias = (Date.now() - new Date(fecha).getTime()) / 86400000;
  if (dias >= 90) return "text-state-critical font-medium";
  if (dias >= 30) return "text-state-warn font-medium";
  return "text-ink-tertiary";
};

const AssignDialog = ({ purchase, onOpenChange }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [tipo, setTipo] = useState("orden");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  React.useEffect(() => {
    if (!purchase || query.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const path = tipo === "orden" ? "/work-orders" : "/projects";
        const { data } = await api.get(path, { params: { q: query, page_size: 8 } });
        setResults((data.items || []).map((it) => ({
          id: it.id, code: it.code,
          label: tipo === "orden" ? `${it.code} · ${it.property_nombre || ""}` : `${it.code} · ${it.company_nombre || it.cliente_nombre || ""}`,
        })));
      } catch (e) { setResults([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [query, tipo, purchase]);

  const mutation = useMutation({
    mutationFn: () => api.patch(`/purchases/${purchase.id}/assign`, tipo === "orden"
      ? { work_order_id: selected.id } : { project_id: selected.id }),
    onSuccess: () => {
      toast.success(t("finance.purchases.assigned"));
      qc.invalidateQueries({ queryKey: ["purchases"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  return (
    <Dialog open={Boolean(purchase)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="purchase-assign-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("finance.purchases.assign_title")}</DialogTitle>
          <DialogDescription>{t("finance.purchases.assign_desc")}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5">
          {["orden", "obra"].map((opt) => (
            <Button key={opt} type="button" size="sm" variant={tipo === opt ? "default" : "outline"}
              data-testid={`purchase-assign-worktype-${opt}`}
              className={tipo === opt ? "h-7 flex-1 bg-ink text-[11.5px] text-paper" : "h-7 flex-1 text-[11.5px]"}
              onClick={() => { setTipo(opt); setSelected(null); setQuery(""); }}>
              {t(`finance.purchases.form.work_type_${opt}`)}
            </Button>
          ))}
        </div>
        {selected ? (
          <div className="flex items-center justify-between border border-line bg-surface-sunken px-2 py-1.5 text-[12.5px]">
            <span className="truncate">{selected.label}</span>
            <button data-testid="purchase-assign-clear" onClick={() => setSelected(null)} className="text-ink-tertiary hover:text-ink">×</button>
          </div>
        ) : (
          <div className="relative">
            <Input data-testid="purchase-assign-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("finance.purchases.form.search_placeholder")} className="h-8 text-[12.5px]" />
            {query.trim().length >= 2 && (
              <div className="absolute z-10 mt-1 w-full border border-line bg-white shadow-sm">
                {results.length ? results.map((r) => (
                  <button key={r.id} data-testid={`purchase-assign-result-${r.id}`}
                    onClick={() => { setSelected(r); setQuery(""); }}
                    className="block w-full truncate px-2.5 py-1.5 text-left text-[12.5px] hover:bg-surface-sunken">
                    {r.label}
                  </button>
                )) : <p className="px-2.5 py-1.5 text-[12px] text-ink-tertiary">{t("finance.purchases.form.no_results")}</p>}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button data-testid="purchase-assign-submit" disabled={!selected || mutation.isPending}
            onClick={() => mutation.mutate()} className="bg-ink text-paper hover:opacity-90">
            {t("finance.purchases.assign")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProrateDialog = ({ open, onOpenChange }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [diasMinimos, setDiasMinimos] = useState(90);
  const [resultado, setResultado] = useState(null);

  const mutation = useMutation({
    mutationFn: () => api.post("/purchases/prorratear", { mes, dias_minimos: Number(diasMinimos) }),
    onSuccess: ({ data }) => {
      setResultado(data);
      qc.invalidateQueries({ queryKey: ["purchases"] });
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setResultado(null); }}>
      <DialogContent data-testid="purchase-prorate-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("finance.purchases.prorate.title")}</DialogTitle>
          <DialogDescription>{t("finance.purchases.prorate.desc", { n: diasMinimos })}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[12px] text-ink-secondary">{t("finance.purchases.prorate.month")}</label>
            <Input data-testid="purchase-prorate-month" type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-[12px] text-ink-secondary">{t("finance.purchases.prorate.min_days")}</label>
            <Input data-testid="purchase-prorate-days" type="number" value={diasMinimos} onChange={(e) => setDiasMinimos(e.target.value)} className="mt-1" />
          </div>
        </div>
        {resultado && (
          <p data-testid="purchase-prorate-result" className="border-l-2 border-state-ok bg-state-ok-soft px-2.5 py-1.5 text-[12.5px] text-state-ok">
            {resultado.prorrateadas
              ? t("finance.purchases.prorate.result", { prorrateadas: resultado.prorrateadas, ordenes: resultado.ordenes, monto: formatMoney(resultado.monto_cents).replace("$", "") })
              : t("finance.purchases.prorate.empty")}
          </p>
        )}
        <DialogFooter>
          <Button data-testid="purchase-prorate-submit" disabled={mutation.isPending} onClick={() => mutation.mutate()} className="bg-ink text-paper hover:opacity-90">
            {t("finance.purchases.prorate.run")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PurchasesTab = () => {
  const { t } = useT();
  const { user, can } = useAuth();
  const [estado, setEstado] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [prorateOpen, setProrateOpen] = useState(false);

  const canProrate = ["owner", "socio_operativo"].includes(user?.rol);

  const { data, isLoading } = useQuery({
    queryKey: ["purchases", estado],
    queryFn: async () => (await api.get("/purchases", { params: { estado: estado === "all" ? undefined : estado, page_size: 100 } })).data,
  });

  const items = data?.items || [];

  return (
    <div data-testid="finance-purchases-tab" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger data-testid="purchase-filter-status" className="h-8 w-[160px] text-[12.5px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("app.all")}</SelectItem>
              <SelectItem value="sin_asignar">{t("finance.purchases.status.sin_asignar")}</SelectItem>
              <SelectItem value="asignada">{t("finance.purchases.status.asignada")}</SelectItem>
              <SelectItem value="prorrateada">{t("finance.purchases.status.prorrateada")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {canProrate && (
            <Button variant="outline" size="sm" data-testid="purchase-prorate-open" onClick={() => setProrateOpen(true)} className="h-8">
              {t("finance.purchases.prorate.title")}
            </Button>
          )}
          {can("finance", "create") && (
            <Button size="sm" data-testid="purchase-new-button" onClick={() => setNewOpen(true)} className="h-8 bg-ink text-paper hover:opacity-90">
              <Plus className="mr-1 h-3.5 w-3.5" /> {t("finance.purchases.new")}
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-[10px] border border-line bg-white">
        {isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-[13px] w-full bg-surface-sunken" />)}</div>
        ) : !items.length ? (
          <p className="p-6 text-center text-[13px] text-ink-tertiary" data-testid="purchases-empty">{t("finance.purchases.empty")}</p>
        ) : (
          <table className="w-full text-[12.5px]" data-testid="purchases-table">
            <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
              <tr>
                <th className="px-3 py-2 text-left">{t("finance.purchases.table.date")}</th>
                <th className="px-3 py-2 text-left">{t("finance.purchases.table.provider")}</th>
                <th className="px-3 py-2 text-left">{t("finance.purchases.table.work")}</th>
                <th className="px-3 py-2 text-right">{t("finance.purchases.table.amount")}</th>
                <th className="px-3 py-2 text-left">{t("finance.purchases.table.status")}</th>
                <th className="px-3 py-2 text-right">{t("finance.purchases.table.receipt")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-line" data-testid={`purchase-row-${p.id}`}>
                  <td className={`px-3 py-1.5 ${p.estado === "sin_asignar" ? ageClass(p.fecha) : ""}`}>{formatDate(p.fecha)}</td>
                  <td className="px-3 py-1.5 truncate">{p.proveedor || p.descripcion || "—"}</td>
                  <td className="px-3 py-1.5">{p.work_order_code || p.project_code || "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(p.monto_cents)}</td>
                  <td className="px-3 py-1.5">
                    {p.anulado ? <StatusChip tone="red" label={t("finance.payments.voided")} /> : (
                      <StatusChip tone={STATUS_TONE[p.estado] || "gray"} label={t(`finance.purchases.status.${p.estado}`)} />
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {p.comprobante?.url && (
                      <a href={p.comprobante.url} target="_blank" rel="noreferrer" data-testid={`purchase-receipt-${p.id}`}
                        className="text-ink-tertiary hover:text-ink"><Download className="inline h-3.5 w-3.5" /></a>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {!p.anulado && p.estado === "sin_asignar" && can("finance", "update") && (
                      <button data-testid={`purchase-assign-${p.id}`} onClick={() => setAssignTarget(p)}
                        className="mr-2 text-[11.5px] font-medium text-brand hover:text-brand-hover">
                        {t("finance.purchases.assign")}
                      </button>
                    )}
                    {!p.anulado && !p.es_contraasiento && can("finance", "update") && (
                      <button data-testid={`purchase-void-${p.id}`} onClick={() => setVoidTarget(p.id)}
                        className="text-[11.5px] font-medium text-ink-tertiary hover:text-state-critical">
                        {t("finance.purchases.void")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PurchaseDialog open={newOpen} onOpenChange={setNewOpen} />
      <AssignDialog purchase={assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)} />
      <ProrateDialog open={prorateOpen} onOpenChange={setProrateOpen} />
      {voidTarget && (
        <VoidDialog open={Boolean(voidTarget)} onOpenChange={(v) => !v && setVoidTarget(null)}
          kind="purchase" id={voidTarget} invalidateKeys={[["purchases"]]} />
      )}
    </div>
  );
};
